import { imageAttemptDeadlineMs } from './ui/image-attempt-deadline.js';

/**
 * Exact-source image recovery for ordinary artwork.
 *
 * A network failure leaves a neutral empty image frame and retries the same
 * source after 1/3/8/20/30 seconds (30 seconds thereafter). It never chooses
 * replacement artwork. Retry ownership ends as soon as the image is disposed
 * or leaves its mounted UI.
 *
 * Each attempt's window — how long a request may stay unanswered before it
 * is dropped and retried — follows the shared policy in
 * ui/image-attempt-deadline.js (30 s, doubling per failed attempt to 120 s).
 * Dropping `src` discards every byte received, so a window shorter than a
 * slow but healthy download restarts it forever; 12 s was that window.
 */
(function installRetryingImage(global) {
    'use strict';

    if (!global || global.MoonfallRetryingImage) return;

    const RETRY_DELAYS_MS = Object.freeze([1000, 3000, 8000, 20000, 30000]);
    const DETACHED_MOUNT_GRACE_MS = 5000;
    const MOON_FUSE_INTERIM_PLACEHOLDER_PATH = '/assets/art_placeholder.webp';
    const controllers = new WeakMap();
    const activeControllers = new Set();

    const activationObserver = typeof global.IntersectionObserver === 'function'
        ? new global.IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                controllers.get(entry.target)?.activate?.();
            });
        }, { rootMargin: '100% 0px' })
        : null;
    const ownershipObserver = typeof global.MutationObserver === 'function'
        && global.document?.documentElement
        ? new global.MutationObserver(() => {
            Array.from(activeControllers).forEach((controller) => {
                controller.verifyOwnership?.();
            });
        })
        : null;
    ownershipObserver?.observe(global.document.documentElement, {
        childList: true,
        subtree: true,
    });

    function decodedPathname(value) {
        try {
            let pathname = new URL(
                String(value || ''),
                global.location?.href || 'http://localhost/',
            ).pathname;
            for (let pass = 0; pass < 3; pass += 1) {
                const decoded = decodeURIComponent(pathname);
                if (decoded === pathname) break;
                pathname = decoded;
            }
            return pathname;
        } catch (_) {
            return '';
        }
    }

    function isMoonFusePlaceholderSource(value) {
        return decodedPathname(value) === MOON_FUSE_INTERIM_PLACEHOLDER_PATH;
    }

    function normalizedSource(value) {
        if (typeof value !== 'string') return '';
        const source = value.trim();
        if (!source) return '';
        try {
            const url = new URL(source, global.location?.href || 'http://localhost/');
            if (!['http:', 'https:', 'blob:', 'data:'].includes(url.protocol)) return '';
            // This file is a semantic Moon Fuse progress state, not generic
            // artwork. Path comparison also rejects query/hash and absolute
            // aliases that would otherwise smuggle it into ordinary cards.
            if (isMoonFusePlaceholderSource(source)) return '';
            return source;
        } catch (_) {
            return '';
        }
    }

    function load(image, sourceValue, options = {}) {
        if (!image || image.tagName !== 'IMG') return null;
        dispose(image, { clearSource: true });

        const source = normalizedSource(sourceValue);
        if (!source) {
            image.removeAttribute('src');
            image.dataset.retryingImageState = 'unavailable';
            return null;
        }

        // A caller may shorten the FIRST window; every later one still grows
        // with the attempts that outlasted it.
        const firstAttemptTimeoutMs = Number.isFinite(options.attemptTimeoutMs)
            && options.attemptTimeoutMs > 0
            ? options.attemptTimeoutMs
            : null;
        const createdAt = Date.now();
        let everConnected = image.isConnected;
        let activated = image.loading !== 'lazy';
        let attemptTimer = null;
        let retryTimer = null;
        let ownershipTimer = null;
        let retryAttempt = 0;
        let wakeListenersBound = false;
        let disposed = false;

        const attemptTimeoutMs = () => (
            firstAttemptTimeoutMs === null
                ? imageAttemptDeadlineMs(retryAttempt)
                : firstAttemptTimeoutMs * (2 ** Math.min(retryAttempt, 2))
        );

        function ownsImage() {
            return !disposed && controllers.get(image) === controller;
        }

        function clearAttemptTimer() {
            if (attemptTimer !== null) clearTimeout(attemptTimer);
            attemptTimer = null;
        }

        function clearOwnershipTimer() {
            if (ownershipTimer !== null) clearTimeout(ownershipTimer);
            ownershipTimer = null;
        }

        function unbindWakeListeners() {
            if (!wakeListenersBound) return;
            wakeListenersBound = false;
            global.removeEventListener('online', wakeRetry);
            global.removeEventListener('pageshow', wakeRetry);
            global.document?.removeEventListener('visibilitychange', wakeRetry);
        }

        function clearRetryTimer() {
            if (retryTimer !== null) clearTimeout(retryTimer);
            retryTimer = null;
            unbindWakeListeners();
        }

        function stopObserving() {
            activationObserver?.unobserve(image);
        }

        function canRetry() {
            if (!ownsImage()) return false;
            if (image.isConnected) {
                everConnected = true;
                return true;
            }
            if (!everConnected && Date.now() - createdAt < DETACHED_MOUNT_GRACE_MS) return true;
            controller.dispose();
            return false;
        }

        function armDetachedOwnershipFallback() {
            if (ownershipTimer !== null || (ownershipObserver && everConnected)) return;
            ownershipTimer = setTimeout(() => {
                ownershipTimer = null;
                if (!canRetry()) return;
                if (
                    !activated
                    && image.loading === 'lazy'
                    && (!ownershipObserver || !everConnected)
                ) {
                    armDetachedOwnershipFallback();
                }
            }, DETACHED_MOUNT_GRACE_MS);
        }

        function bindWakeListeners() {
            if (wakeListenersBound) return;
            wakeListenersBound = true;
            global.addEventListener('online', wakeRetry, { passive: true });
            global.addEventListener('pageshow', wakeRetry, { passive: true });
            global.document?.addEventListener('visibilitychange', wakeRetry, { passive: true });
        }

        function scheduleRetry() {
            if (!canRetry()) return;
            clearAttemptTimer();
            stopObserving();
            image.removeAttribute('src');
            image.dataset.retryingImageState = 'retrying';
            const delay = RETRY_DELAYS_MS[Math.min(retryAttempt, RETRY_DELAYS_MS.length - 1)];
            retryAttempt += 1;
            image.dataset.retryingImageAttempt = String(retryAttempt);
            clearRetryTimer();
            retryTimer = setTimeout(attempt, delay);
            bindWakeListeners();
        }

        function armAttemptDeadline() {
            clearAttemptTimer();
            if (!activated && image.loading === 'lazy') {
                if (activationObserver) {
                    activationObserver.observe(image);
                    armDetachedOwnershipFallback();
                    return;
                }
                activated = true;
            }
            attemptTimer = setTimeout(scheduleRetry, attemptTimeoutMs());
        }

        function attempt() {
            if (!canRetry()) return;
            if (global.document?.visibilityState === 'hidden') {
                clearRetryTimer();
                retryTimer = setTimeout(attempt, RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1]);
                bindWakeListeners();
                return;
            }
            clearRetryTimer();
            image.dataset.retryingImageState = 'loading';
            image.setAttribute('src', source);
            if (
                image.complete
                && image.naturalWidth > 0
                && image.naturalHeight > 0
            ) {
                settle();
                return;
            }
            armAttemptDeadline();
        }

        function settle() {
            if (!ownsImage()) return;
            if (
                image.getAttribute('src') !== source
                || !(image.naturalWidth > 0)
                || !(image.naturalHeight > 0)
            ) return;
            clearAttemptTimer();
            clearRetryTimer();
            clearOwnershipTimer();
            stopObserving();
            image.removeEventListener('load', settle);
            image.removeEventListener('error', fail);
            image.dataset.retryingImageState = 'loaded';
            delete image.dataset.retryingImageAttempt;
            controllers.delete(image);
            activeControllers.delete(controller);
            disposed = true;
        }

        function fail() {
            if (!ownsImage()) return;
            if (image.getAttribute('src') !== source) return;
            activated = true;
            scheduleRetry();
        }

        function wakeRetry() {
            if (!ownsImage() || retryTimer === null) return;
            if (global.document?.visibilityState === 'hidden') return;
            attempt();
        }

        const controller = {
            activate() {
                if (!ownsImage()) return;
                activated = true;
                clearOwnershipTimer();
                stopObserving();
                if (
                    image.complete
                    && image.naturalWidth > 0
                    && image.naturalHeight > 0
                ) settle();
                else armAttemptDeadline();
            },
            verifyOwnership() {
                canRetry();
            },
            dispose({ clearSource = true } = {}) {
                if (disposed) return;
                disposed = true;
                clearAttemptTimer();
                clearRetryTimer();
                clearOwnershipTimer();
                stopObserving();
                image.removeEventListener('load', settle);
                image.removeEventListener('error', fail);
                if (clearSource) image.removeAttribute('src');
                if (controllers.get(image) === controller) controllers.delete(image);
                activeControllers.delete(controller);
            },
        };

        controllers.set(image, controller);
        activeControllers.add(controller);
        image.addEventListener('load', settle);
        image.addEventListener('error', fail);
        attempt();
        return controller;
    }

    function dispose(image, options) {
        controllers.get(image)?.dispose?.(options);
    }

    function disposeAll() {
        Array.from(activeControllers).forEach((controller) => controller.dispose());
    }

    global.MoonfallRetryingImage = Object.freeze({
        load,
        dispose,
        disposeAll,
        isMoonFusePlaceholderSource,
        retryDelaysMs: RETRY_DELAYS_MS,
    });
})(typeof window !== 'undefined' ? window : null);
